import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {openConfirmDialog, showNotification, Switch} from 'platform/components';

import {useRef} from 'react';

import {includes, not} from 'ramda';
import {isNotNil, noop} from 'ramda-adjunct';

import {useGetEffectiveValueWithoutReferenceQuery} from '@dms/api/settings';
import {getApiErrorMessages} from '@dms/api/utils';
import {OrderActionKey, usePostOrderActionMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {useServiceCaseDetailActions} from '@dms/shared-aftersales';

import {isNotNilOrEmpty, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useRefreshDocumentCount} from '../../../../../hooks/useRefreshDocumentCount';
import {ServiceCaseOrder} from '../../../../../types/OrderType';
import {ORDER_ACTION_PREFIX} from '../constants/actionKeys';
import {useHandleServiceOrderMandatoryFieldsError} from '../hooks/useHandleServiceOrderMandatoryFieldsError';
import {useOrderActions} from '../hooks/useOrderActions';

const LOCKED_ORDER_STATE: ServiceCaseOrder['state'] = 'LOCKED';
const INACTIVE_ORDER_STATES: ServiceCaseOrder['state'][] = ['ARCHIVED', 'CANCELLED'];

// The customer message that fires "Immediately when the service order is set to Ready for invoice".
const READY_TO_INVOICE_MESSAGE_SETTING_ID = 'workshop-service_order_completed-setting';
// `useDialogsState` dedupes on the id it was handed; an undefined one stacks a second dialog.
const READY_TO_INVOICE_CONFIRM_DIALOG_ID = 'readyToInvoiceConfirm';

interface ReadyToInvoiceSwitchProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  serviceOrder: ServiceCaseOrder;
  vehicleId: string;
  isSaveRequestPending: boolean;
  isOrderDataRefetching: boolean;
  refreshServiceOrderDocumentDataGrid: () => void;
}

/**
 * Lock/unlock toggle in the order card header. It is the only control for the `lock` and `unlock`
 * order actions - both keys are filtered out of the card's three-dot menu and its primary button.
 */
export function ReadyToInvoiceSwitch(props: ReadyToInvoiceSwitchProps) {
  const [postOrderAction] = usePostOrderActionMutation();
  const {executeAction, isActionPending, isAnyActionPending} = useServiceCaseDetailActions();

  const {orderActions} = useOrderActions({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    embeddedOrderActions: props.serviceOrder?.orderActions,
  });

  const handleMandatoryFieldsError = useHandleServiceOrderMandatoryFieldsError({
    orderId: props.serviceOrderId,
    serviceOrderVariantId: props.serviceOrder?.serviceOrderVariant?.serviceOrderVariantId,
    vehicleId: props.vehicleId,
  });

  const refreshDocumentCount = useRefreshDocumentCount(props.serviceCaseId);

  // A ref, not pending state: `openConfirmDialog` freezes `onConfirm` at click time.
  const inFlightActionRef = useRef<Promise<unknown> | null>(null);

  // Must stay above the early return - a conditional hook otherwise.
  const {data: readyToInvoiceMessageSettings, isFetching: isMessageSettingsFetching} =
    useGetEffectiveValueWithoutReferenceQuery(
      {settingId: READY_TO_INVOICE_MESSAGE_SETTING_ID},
      // Tag invalidation does not cross tabs, so nothing else clears a stale value.
      {refetchOnFocus: true}
    );

  // The settings screen's own "Message and channel enabled" requirement.
  const willNotifyCustomer =
    readyToInvoiceMessageSettings?.enabled === true &&
    (readyToInvoiceMessageSettings?.smsEnabled === true ||
      readyToInvoiceMessageSettings?.emailEnabled === true);

  const isLocked = props.serviceOrder?.state === LOCKED_ORDER_STATE;

  const actionKey: OrderActionKey = isLocked ? 'unlock' : 'lock';
  const action = orderActions?.options?.find((option) => option?.key === actionKey);
  // `optionClick` and `options` are independently optional, so the action counts as offered when
  // it appears in either one.
  const isActionOffered =
    (isNotNil(action) && action.style !== 'hidden') || orderActions?.optionClick === actionKey;
  const isOrderActive = not(includes(props.serviceOrder?.state, INACTIVE_ORDER_STATES));

  if (!isActionOffered || !isOrderActive) {
    return null;
  }

  const pendingActionKey = `${ORDER_ACTION_PREFIX}${actionKey}/${props.serviceOrderId}`;
  // The POST resolves before the orders list reports the new state. Comparing the requested state
  // against the rendered one cannot close that window, because a still-pending refetch and a
  // refetch that landed on the old value look identical. Staying busy until the list stops
  // fetching ends the window when fresh data arrives, whatever that data says.
  const isBusy = isActionPending(pendingActionKey) || props.isOrderDataRefetching;

  // Settles fulfilled - `ConfirmDialog` awaits it without a try/catch. Re-entry returns the running
  // promise so a second Enter waits for the original action instead of closing the dialog early.
  const performAction = () => {
    if (isNotNil(inFlightActionRef.current)) {
      return inFlightActionRef.current;
    }

    const action = executeAction(pendingActionKey, () =>
      postOrderAction({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        actionKey,
      }).unwrap()
    )
      .then(() => {
        refreshDocumentCount();
        props.refreshServiceOrderDocumentDataGrid();

        showNotification.success(i18n.t(`entity.order.notifications.success-${actionKey}`), {
          // Distinct from OrderActions' own `successNotification` id - OrderCard hands both
          // components the same unsuffixed card test id.
          'data-testid': suffixTestId('readyToInvoiceSuccessNotification', props),
        });
      })
      .catch((error: FetchBaseQueryError) =>
        // Returned, not fired and forgotten - the handler is async and opens its own modal.
        handleMandatoryFieldsError(error, 'BEFORE_OPENING', {
          silent: true,
          skipQueryStateUpdate: true,
          callback: () =>
            showNotification.error(
              getApiErrorMessages(error, i18n.t(`entity.order.notifications.error-${actionKey}`))
            ),
        }).catch(noop)
      )
      .finally(() => {
        inFlightActionRef.current = null;
      });

    inFlightActionRef.current = action;

    return action;
  };

  const handleChange = () => {
    // Only activation warns - "Cancel Ready to invoice" sends nothing, so it stays immediate.
    if (actionKey === 'lock' && willNotifyCustomer) {
      openConfirmDialog({
        id: READY_TO_INVOICE_CONFIRM_DIALOG_ID,
        text: i18n.t('entity.order.labels.lock'),
        'data-testid': suffixTestId('readyToInvoiceConfirm', props),
        onConfirm: performAction,
      });

      return;
    }

    performAction();
  };

  const label = i18n.t('entity.order.actions.lock');

  return (
    // `Switch` calls `onChange` without an event, so it cannot stop its own propagation - without
    // this wrapper the click bubbles to the card's `Clickable` and collapses the card.
    <div onClick={(event) => event.stopPropagation()}>
      <Switch
        label={label}
        // Every card renders the same visible label, so the order number keeps the controls
        // distinguishable for screen readers.
        aria-label={[label, props.serviceOrder?.number].filter(isNotNilOrEmpty).join(' · ')}
        value={isLocked}
        onChange={handleChange}
        isDisabled={
          action?.style === 'disabled' ||
          isAnyActionPending ||
          props.isSaveRequestPending ||
          // A click decided on a stale or half-loaded gate sends the message with no warning.
          isMessageSettingsFetching ||
          // `Switch` takes `data-disabled` from `isDisabled` alone; the busy window would look live.
          isBusy
        }
        isLoading={isBusy}
        data-testid={suffixTestId('readyToInvoiceSwitch', props)}
      />
    </div>
  );
}
